import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { createPortal } from 'react-dom';
import { useCruises, Cruise } from '../context/CruiseContext';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { useAppSettings } from '../context/AppSettingsContext';
import { useFriends } from '../context/FriendContext';
import { AggregatedCruise } from '../services/friendService';
import { SHOW_FRIENDS_BUTTON } from '../config';
import { PlusCircle, Search, Calendar as CalendarIcon, MapPin, Sunrise, SunsetIcon, ExternalLink, Tag, Clock, Dice3 as Dice, AlertCircle, CheckCircle, X, Waves, Anchor, Navigation, PartyPopper, Home, Compass, BarChart, Share2, Download, Loader2, Info, Wifi, GlassWater, Users } from 'lucide-react';
import { parseISO, eachDayOfInterval, isSameDay, differenceInDays, startOfDay, addDays } from 'date-fns';
import { getCruiseEndTime, isCruiseCurrentlySailing } from '../utils/dateUtils';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';
import { motion } from 'framer-motion';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import toast from 'react-hot-toast';
import CustomCruiseIcon from '../components/CustomCruiseIcon';
import LazyImage from '../components/LazyImage';
import { DashboardSkeleton } from '../components/LoadingSkeleton';
import ShareCountdownImageFeed from '../components/ShareCountdownImageFeed';
import ShareCountdownImageStories from '../components/ShareCountdownImageStories';
import { shareCountdownImage } from '../utils/shareUtils';
import { useCruiseItineraryTitle } from '../hooks/useCruiseItineraryTitle';
import { debug } from '../utils/debug';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

const Dashboard = () => {
  // State to manage skeleton display timing
  const [dataLoaded, setDataLoaded] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  
  // State for scroll position memory
  const [rememberedScrollY, setRememberedScrollY] = useState(0);

  // State for load more functionality with persistent storage
  const [visiblePastCount, setVisiblePastCount] = useState(() => {
    const stored = localStorage.getItem('dashboardVisiblePastCount');
    if (!stored) return 10;
    const parsed = parseInt(stored, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 10;
  });
  const { cruises, loading, deleteCruise } = useCruises();
  const { formatDate, formatRelativeTime, formatTimeString, getCalendarType } = useDisplayPreferences();
  const { getShipImageUrl, getShipLocationUrl, getShipDeckplanUrl } = useCruiseMetadata();
  const { settings, loading: settingsLoading } = useAppSettings();
  const isNative = Capacitor.getPlatform() !== 'web';
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  // Debug logging for loading states
  debug.log('Dashboard render:', {
    loading,
    settingsLoading,
    cruisesCount: cruises?.length || 0,
    cruisesExists: !!cruises,
    timestamp: new Date().toISOString()
  });

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCruises, setFilteredCruises] = useState<Cruise[]>([]);
  const [selectedDate, setSelectedDate] = useState<Value>(new Date());
  const [cruiseDates, setCruiseDates] = useState<Date[]>([]);
  const [selectedDateCruises, setSelectedDateCruises] = useState<Cruise[]>([]);
  const [nextCruise, setNextCruise] = useState<Cruise | null>(null);
  const [daysUntilNextCruise, setDaysUntilNextCruise] = useState<number | null>(null);
  const nextCruiseItineraryTitle = useCruiseItineraryTitle(nextCruise);
  const [activeCruise, setActiveCruise] = useState<Cruise | null>(null);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [recentCruise, setRecentCruise] = useState<Cruise | null>(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedCruiseForCompletion, setSelectedCruiseForCompletion] = useState<Cruise | null>(null);
  
  // State to track which external link button is being touched (for mobile active state fix)
  const [activeButton, setActiveButton] = useState<string | null>(null);

  // Share countdown state
  const [isGeneratingShare, setIsGeneratingShare] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareFormat, setShareFormat] = useState<'feed' | 'stories'>('feed');
  const [imageGenerated, setImageGenerated] = useState(false);
  const shareImageRef = useRef<HTMLDivElement>(null);

  // Friends state & companion calculations
  const { friends, loading: friendsLoading, getAllFriendsCruises } = useFriends();
  const [friendsCruises, setFriendsCruises] = useState<AggregatedCruise[]>([]);
  const [friendsCruisesLoaded, setFriendsCruisesLoaded] = useState(false);

  // Memoized friend calculations
  const currentlySailingFriendEntries = useMemo(() => {
    const today = startOfDay(new Date());
    return friendsCruises.filter((ac) => {
      const dep = startOfDay(parseISO(ac.cruise.departureDate));
      const ret = startOfDay(parseISO(ac.cruise.returnDate));
      return today >= dep && today < ret;
    });
  }, [friendsCruises]);

  const activeCruiseCompanions = useMemo(() => {
    if (!activeCruise) return [];
    const matchingGroup = friendsCruises.find(
      (ac) =>
        ac.cruise.shipName === activeCruise.shipName &&
        ac.cruise.departureDate === activeCruise.departureDate
    );
    return matchingGroup ? matchingGroup.friends : [];
  }, [activeCruise, friendsCruises]);

  const getCompanionsForCruise = (shipName: string, departureDate: string) => {
    const matchingGroup = friendsCruises.find(
      (ac) => ac.cruise.shipName === shipName && ac.cruise.departureDate === departureDate
    );
    return matchingGroup ? matchingGroup.friends : [];
  };

  // Track when all data is actually loaded (cruises, settings, AND friends cruises)
  useEffect(() => {
    if (!loading && !settingsLoading && cruises && friendsCruisesLoaded) {
      setDataLoaded(true);
    } else {
      setDataLoaded(false);
    }
  }, [loading, settingsLoading, cruises, friendsCruisesLoaded]);

  // Ensure skeleton shows for minimum duration
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, 500); // 500ms minimum skeleton display

    return () => clearTimeout(timer);
  }, []);

  // Clear active button state when app resumes (fixes stuck active state after external link opens)
  useEffect(() => {
    if (!isNative) return;

    const handleResume = () => {
      setActiveButton(null);
    };

    const listenerPromise = CapacitorApp.addListener('resume', handleResume).catch(() => {
      // Listener setup failed, ignore
    });

    return () => {
      listenerPromise.then((listener) => {
        try {
          listener?.remove?.();
        } catch {
          /* ignore */
        }
      });
    };
  }, [isNative]);

  useEffect(() => {
    let isMounted = true;
    if (!friendsLoading) {
      if (SHOW_FRIENDS_BUTTON && friends.length > 0) {
        getAllFriendsCruises(true)
          .then((res) => {
            if (isMounted) {
              setFriendsCruises(res);
              setFriendsCruisesLoaded(true);
            }
          })
          .catch((err) => {
            console.error('Error fetching friends cruises for dashboard:', err);
            if (isMounted) setFriendsCruisesLoaded(true);
          });
      } else {
        setFriendsCruisesLoaded(true);
      }
    }
    return () => {
      isMounted = false;
    };
  }, [friendsLoading, friends, getAllFriendsCruises]);

  // Read stored scroll position on component mount
  useEffect(() => {
    const storedScrollY = sessionStorage.getItem('dashboardScrollY');
    if (storedScrollY) {
      const parsed = parseInt(storedScrollY, 10);
      if (Number.isFinite(parsed) && parsed >= 0) {
        setRememberedScrollY(parsed);
      }
      sessionStorage.removeItem('dashboardScrollY'); // Clear after reading
    }
  }, []);

  // Persist visiblePastCount to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('dashboardVisiblePastCount', visiblePastCount.toString());
  }, [visiblePastCount]);



  // Restore scroll position before paint to avoid flash of top of page (useLayoutEffect runs after DOM update, before browser paint)
  useLayoutEffect(() => {
    if (dataLoaded && minTimeElapsed && rememberedScrollY > 0) {
      window.scrollTo(0, rememberedScrollY);
      setRememberedScrollY(0); // Reset to prevent re-scrolling
    }
  }, [dataLoaded, minTimeElapsed, rememberedScrollY]);

  // Enhanced search function
  const searchCruises = (cruises: Cruise[], searchTerm: string): Cruise[] => {
    if (!searchTerm.trim()) {
      return cruises;
    }

    const term = searchTerm.toLowerCase();

    return cruises.filter((cruise) => {
      // Basic cruise information
      const basicMatch = 
        cruise.cruiseLine.toLowerCase().includes(term) ||
        cruise.shipName.toLowerCase().includes(term) ||
        cruise.departurePort.toLowerCase().includes(term) ||
        cruise.cabinType.toLowerCase().includes(term) ||
        (cruise.cabinNumber && cruise.cabinNumber.toLowerCase().includes(term)) ||
        (cruise.bookingNumber && cruise.bookingNumber.toLowerCase().includes(term)) ||
        (cruise.notes && cruise.notes.toLowerCase().includes(term));

      // Port searches
      const portMatch = cruise.ports?.some(port => 
        port.name?.toLowerCase().includes(term) ||
        port.excursion?.toLowerCase().includes(term)
      ) || false;

      // Travel details searches
      const travelMatch = cruise.travelDetails?.some(travel => {
        const commonFields = 
          travel.company?.toLowerCase().includes(term) ||
          travel.reservationNumber?.toLowerCase().includes(term) ||
          travel.departureLocation?.toLowerCase().includes(term) ||
          travel.arrivalLocation?.toLowerCase().includes(term);

        // Type-specific fields
        const typeSpecificFields = 
          (travel.type === 'airplane' && travel.flightNumber?.toLowerCase().includes(term)) ||
          (travel.type === 'train' && travel.trainNumber?.toLowerCase().includes(term)) ||
          (travel.type === 'rental-car' && (
            travel.carType?.toLowerCase().includes(term) ||
            travel.pickupLocation?.toLowerCase().includes(term) ||
            travel.returnLocation?.toLowerCase().includes(term)
          ));

        return commonFields || typeSpecificFields;
      }) || false;

      return basicMatch || portMatch || travelMatch;
    });
  };

  // Combined useEffect for localStorage check and welcome back message logic
  useEffect(() => {
    // First, check localStorage for dismissed welcome back
    const dismissedData = localStorage.getItem('welcomeBackDismissed');
    let shouldBeDismissed = false;
    
    if (dismissedData) {
      try {
        const { timestamp } = JSON.parse(dismissedData);
        const dismissedTime = new Date(timestamp);
        const now = new Date();
        const hoursSinceDismissed = (now.getTime() - dismissedTime.getTime()) / (1000 * 60 * 60);
        
        // If dismissed less than 24 hours ago, keep it dismissed
        if (hoursSinceDismissed < 24) {
          shouldBeDismissed = true;
        } else {
          // Clear expired dismissal data
          localStorage.removeItem('welcomeBackDismissed');
        }
      } catch (error) {
        console.error('Error parsing welcome back dismissal data:', error);
        localStorage.removeItem('welcomeBackDismissed');
      }
    }

    // Then, check for welcome back modal logic (only if not dismissed)
    if (cruises && !shouldBeDismissed) {
      const now = new Date();
      const today = startOfDay(now);
      
      // Check for recently completed cruises (within last 7 days and not marked as completed)
      // Changed to include return date (daysSinceReturn >= 0) so modal shows when cruise returns
      const recentlyCompletedCruises = cruises.filter(cruise => {
        const returnDate = parseISO(cruise.returnDate);
        const daysSinceReturn = differenceInDays(today, startOfDay(returnDate));
        return !isCruiseCurrentlySailing(cruise, now) && daysSinceReturn >= 0 && daysSinceReturn <= 7 && !cruise.completed;
      });

      if (recentlyCompletedCruises.length > 0) {
        const mostRecent = recentlyCompletedCruises.sort((a, b) => 
          parseISO(b.returnDate).getTime() - parseISO(a.returnDate).getTime()
        )[0];
        setRecentCruise(mostRecent);
        setShowWelcomeBack(true);
      } else {
        setShowWelcomeBack(false);
        setRecentCruise(null);
      }
    } else if (shouldBeDismissed) {
      // If dismissed, ensure modal is not shown
      setShowWelcomeBack(false);
      setRecentCruise(null);
    }
  }, [cruises]);

  // Effect to manage body scroll when modals are open
  useEffect(() => {
    // Only apply modal-open class when modals are actually rendered in the DOM
    const hasAnyModalOpen = (showWelcomeBack && recentCruise) || showCompletionModal || showShareModal;
    
    if (hasAnyModalOpen) {
      // Do nothing - let's see if the modal itself is causing the issue
      debug.log('Modal is open, but not changing body styles');
    } else {
      // Do nothing
      debug.log('Modal is closed');
    }
  }, [showWelcomeBack, recentCruise, showCompletionModal, showShareModal]);

  // Main useEffect for cruise data processing and search filtering
  useEffect(() => {
    if (cruises) {
      const now = new Date();
      const today = startOfDay(now);
      
      // Find active cruise (currently sailing) using precise arrival time boundaries
      const sortedCruises = [...cruises].sort(
        (a, b) => parseISO(a.departureDate).getTime() - parseISO(b.departureDate).getTime()
      );
      
      const currentlyActive = sortedCruises.find(cruise => isCruiseCurrentlySailing(cruise, now));
      setActiveCruise(currentlyActive || null);
      
      // Filter and sort upcoming cruises (exclude active cruise)
      const upcomingCruises = cruises
        .filter(cruise => {
          if (currentlyActive && cruise.id === currentlyActive.id) return false;
          return getCruiseEndTime(cruise) > now;
        })
        .sort((a, b) => parseISO(a.departureDate).getTime() - parseISO(b.departureDate).getTime());

      // Set the next cruise and calculate days until departure
      if (upcomingCruises.length > 0) {
        const next = upcomingCruises[0];
        setNextCruise(next);
        const days = differenceInDays(startOfDay(parseISO(next.departureDate)), today);
        setDaysUntilNextCruise(days);
      } else {
        setNextCruise(null);
        setDaysUntilNextCruise(null);
      }

      // Apply enhanced search filtering
      const searchResults = searchCruises(cruises, searchTerm);
      setFilteredCruises(
        searchResults.sort((a, b) => {
          const dateA = parseISO(a.departureDate);
          const dateB = parseISO(b.departureDate);
          return dateA.getTime() - dateB.getTime();
        })
      );

      // Generate all dates for cruises
      const allDates: Date[] = [];
      cruises.forEach(cruise => {
        try {
          const startDate = parseISO(cruise.departureDate);
          const endDate = parseISO(cruise.returnDate);
          
          // Validate dates
          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            debug.warn(`Invalid dates for cruise ${cruise.shipName}: ${cruise.departureDate} - ${cruise.returnDate}`);
            return;
          }
          
          // Check if start date is before end date
          if (startDate > endDate) {
            debug.warn(`Start date after end date for cruise ${cruise.shipName}: ${cruise.departureDate} - ${cruise.returnDate}`);
            return;
          }
          
          const dates = eachDayOfInterval({
            start: startDate,
            end: endDate
          });
          allDates.push(...dates);
        } catch (error) {
          console.error(`Error processing dates for cruise ${cruise.shipName}:`, error);
        }
      });
      setCruiseDates(allDates);
    }
  }, [cruises, searchTerm]);

  useEffect(() => {
    if (selectedDate instanceof Date) {
      const cruisesOnDate = cruises.filter(cruise => {
        const startDate = parseISO(cruise.departureDate);
        const endDate = parseISO(cruise.returnDate);
        const dateToCheck = selectedDate;
        return dateToCheck >= startDate && dateToCheck <= endDate;
      });
      setSelectedDateCruises(cruisesOnDate);
    }
  }, [selectedDate, cruises]);

  const handleCruiseCompletion = (cruise: Cruise, completed: boolean) => {
    if (completed) {
      // Navigate to post-cruise details page
      navigate(`/post-cruise-details/${cruise.id}`);
    } else {
      // Show confirmation modal for deletion
      setSelectedCruiseForCompletion(cruise);
      setShowCompletionModal(true);
    }
  };

  const handleDeleteCruise = async () => {
    if (!selectedCruiseForCompletion) return;
    
    try {
      await deleteCruise(selectedCruiseForCompletion.id);
      setShowCompletionModal(false);
      setSelectedCruiseForCompletion(null);
    } catch (error) {
      console.error('Error deleting cruise:', error);
    }
  };

  const dismissWelcomeBack = () => {
    setShowWelcomeBack(false);
    setRecentCruise(null);
    
    // Store dismissal timestamp in localStorage for 24-hour persistence
    const dismissalData = {
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('welcomeBackDismissed', JSON.stringify(dismissalData));
  };

  const handleShareCountdown = () => {
    // Reset state and open the format selection modal
    setImageGenerated(false);
    setShowShareModal(true);
  };

  const handleGenerateAndShare = async () => {
    if (!nextCruise || !shareImageRef.current || daysUntilNextCruise === null) return;

    try {
      setIsGeneratingShare(true);

      // Calculate days fresh at the time of sharing to ensure accuracy
      const now = new Date();
      const today = startOfDay(now);
      const daysUntilDeparture = differenceInDays(startOfDay(parseISO(nextCruise.departureDate)), today);
      
      await shareCountdownImage(shareImageRef, {
        shipName: nextCruise.shipName,
        cruiseLine: nextCruise.cruiseLine,
        daysUntilDeparture,
        format: shareFormat,
        itineraryTitle: nextCruiseItineraryTitle
      });
      
      if (Capacitor.isNativePlatform()) {
        // On native platforms, the share sheet opens directly, so dismiss the modal
        setShowShareModal(false);
        // No toast message needed - the native share sheet provides its own feedback
      } else {
        toast.success('Countdown image downloaded!');
        // Mark image as generated and keep modal open for sharing options
        setImageGenerated(true);
      }
    } catch (error) {
      console.error('Error generating share image:', error);
      
      // On native platforms, don't show error toast for user cancellation
      if (Capacitor.isNativePlatform()) {
        // Check if it's a user cancellation (common error messages)
        const errorMessage = error instanceof Error ? error.message.toLowerCase() : '';
        if (errorMessage.includes('cancelled') || errorMessage.includes('canceled') || errorMessage.includes('user')) {
          // User cancelled - no toast needed
          return;
        }
      }
      
      // Show error toast for actual errors or on web platform
      toast.error('Failed to share countdown image');
    } finally {
      setIsGeneratingShare(false);
    }
  };

  const handleNativeShare = async () => {
    if (!nextCruise || daysUntilNextCruise === null) return;

    const shareText = `${daysUntilNextCruise} days until my cruise on the ${nextCruise.shipName}! 🚢 Track your cruise adventures with Cruising Along.`;
    const shareUrl = 'https://keepcruisingalong.com';

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Cruise Countdown',
          text: shareText,
          url: shareUrl
        });
        setShowShareModal(false);
      } catch (error) {
        // User cancelled or error occurred
        debug.log('Share cancelled or failed:', error);
      }
    }
  };

  const handleSocialShare = (platform: 'twitter' | 'facebook') => {
    if (!nextCruise || daysUntilNextCruise === null) return;

    const shareText = `${daysUntilNextCruise} days until my cruise on the ${nextCruise.shipName}! 🚢 Track your cruise adventures with Cruising Along.`;
    const shareUrl = 'https://keepcruisingalong.com';
    
    let url = '';
    
    if (platform === 'twitter') {
      url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    } else if (platform === 'facebook') {
      url = `https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(shareText)}&u=${encodeURIComponent(shareUrl)}`;
    }
    
    if (url) {
      window.open(url, '_blank', 'width=600,height=400');
      setShowShareModal(false);
    }
  };

  const tileClassName = ({ date }: { date: Date }) => {
    if (cruiseDates.some(cruiseDate => isSameDay(cruiseDate, date))) {
      return 'bg-blue-100 text-gray-900 hover:bg-blue-200';
    }
    return null;
  };

  // Get today's port information for active cruise
  const getTodaysPort = (cruise: Cruise) => {
    const today = startOfDay(new Date());
    return cruise.ports?.find(port => {
      if (!port.date) return false;
      const portDate = startOfDay(parseISO(port.date));
      return isSameDay(portDate, today);
    });
  };

  // Get tomorrow's port information for active cruise
  const getTomorrowsPort = (cruise: Cruise) => {
    const tomorrow = addDays(startOfDay(new Date()), 1);
    return cruise.ports?.find(port => {
      if (!port.date) return false;
      const portDate = startOfDay(parseISO(port.date));
      return isSameDay(portDate, tomorrow);
    });
  };

  // Calculate current day of cruise
  const getCurrentCruiseDay = (cruise: Cruise) => {
    const today = startOfDay(new Date());
    const departureDate = startOfDay(parseISO(cruise.departureDate));
    return differenceInDays(today, departureDate) + 1;
  };

  // Check if today is departure day
  const isDepartureDay = (cruise: Cruise) => {
    const today = startOfDay(new Date());
    const departureDate = startOfDay(parseISO(cruise.departureDate));
    return isSameDay(today, departureDate);
  };

  // Check if tomorrow is return day
  const isTomorrowReturnDay = (cruise: Cruise) => {
    const tomorrow = addDays(startOfDay(new Date()), 1);
    const returnDate = startOfDay(parseISO(cruise.returnDate));
    return isSameDay(tomorrow, returnDate);
  };

  // Check if today is return day (disembarkation day)
  const isReturnDay = (cruise: Cruise) => {
    const today = startOfDay(new Date());
    const returnDate = startOfDay(parseISO(cruise.returnDate));
    return isSameDay(today, returnDate);
  };

  // Count sea days from the day after departure date up to (and including) a given date
  // Excludes departure day since it's always "Boarding Day!"
  const countSeaDaysUpTo = (cruise: Cruise, targetDate: Date) => {
    const departureDate = startOfDay(parseISO(cruise.departureDate));
    const checkDate = startOfDay(targetDate);
    
    // Start counting from the day AFTER departure (departure day is always boarding day)
    const dayAfterDeparture = addDays(departureDate, 1);
    
    // If target date is before or on departure day, return 0 (no sea days yet)
    if (checkDate <= departureDate) {
      return 0;
    }
    
    // Get all days from day after departure to target date
    const allDays = eachDayOfInterval({
      start: dayAfterDeparture,
      end: checkDate
    });
    
    // Count days that don't have a port
    let seaDayCount = 0;
    allDays.forEach(day => {
      const hasPort = cruise.ports?.some(port => {
        if (!port.date) return false;
        const portDate = startOfDay(parseISO(port.date));
        return isSameDay(portDate, day);
      });
      
      if (!hasPort) {
        seaDayCount++;
      }
    });
    
    return seaDayCount;
  };

  // Get today's agenda content based on special days
  const getTodaysAgendaContent = (cruise: Cruise) => {
    const todaysPort = getTodaysPort(cruise);

    // On disembarkation day, show return info before any other checks
    if (isReturnDay(cruise)) {
      const returnPort = cruise.ports?.find(p => p.date === cruise.returnDate);
      return {
        icon: Home,
        iconColor: 'text-orange-300',
        title: 'Disembarkation Day',
        subtitle: 'Time to head home!',
        description: 'Pack up, clear your cabin, and prepare for your journey home.',
        times: returnPort?.arrivalTime ? { arrival: returnPort.arrivalTime, departure: undefined as string | undefined } : undefined
      };
    }
    
    if (isDepartureDay(cruise)) {
      return {
        icon: PartyPopper,
        iconColor: 'text-green-400',
        title: 'Boarding Day!',
        subtitle: 'Your cruise adventure begins today',
        description: 'Check-in, explore the ship, and get ready to sail!'
      };
    }

    if (todaysPort) {
      return {
        icon: Compass,
        iconColor: 'text-green-300',
        title: todaysPort.name,
        subtitle: formatDate(parseISO(todaysPort.date), { includeWeekday: true, shortFormat: true }),
        description: null,
        times: {
          arrival: todaysPort.arrivalTime,
          departure: todaysPort.departureTime
        },
        excursion: todaysPort.excursion
      };
    }

    // Today is a sea day - check if it's the first one
    const today = startOfDay(new Date());
    const seaDayCount = countSeaDaysUpTo(cruise, today);
    const isFirstSeaDay = seaDayCount === 1;

    return {
      icon: Waves,
      iconColor: 'text-blue-300',
      title: 'Sea Day',
      subtitle: isFirstSeaDay ? 'Enjoy your first day at sea' : 'Another fun day at sea',
      description: null
    };
  };

  // Get tomorrow's agenda content based on special days.
  // Returns null on disembarkation day when there is no back-to-back cruise.
  const getTomorrowsAgendaContent = (cruise: Cruise) => {
    const tomorrowsPort = getTomorrowsPort(cruise);

    // On disembarkation day, check for a back-to-back cruise
    if (isReturnDay(cruise)) {
      const today = startOfDay(new Date());
      const tomorrow = addDays(today, 1);

      // Find another cruise that departs today (same-day b2b) or tomorrow (consecutive b2b)
      const nextCruise = cruises?.find(c => {
        if (c.id === cruise.id) return false;
        const depDate = startOfDay(parseISO(c.departureDate));
        return isSameDay(depDate, today) || isSameDay(depDate, tomorrow);
      });

      if (nextCruise) {
        const nextDepDate = startOfDay(parseISO(nextCruise.departureDate));
        const boardsToday = isSameDay(nextDepDate, today);
        return {
          icon: PartyPopper,
          iconColor: 'text-green-300',
          title: nextCruise.shipName,
          subtitle: boardsToday ? 'Boarding Later Today!' : 'Boarding Tomorrow!',
          description: `Your back-to-back adventure continues on ${nextCruise.cruiseLine}!`
        };
      }

      // No back-to-back — hide the Tomorrow column
      return null;
    }
    
    if (isTomorrowReturnDay(cruise)) {
      return {
        icon: Home,
        iconColor: 'text-orange-300',
        title: 'Disembarkation',
        subtitle: 'Return to port tomorrow',
        description: 'Pack up and prepare for your journey home'
      };
    }

    if (tomorrowsPort) {
      return {
        icon: Navigation,
        iconColor: 'text-yellow-300',
        title: tomorrowsPort.name,
        subtitle: formatDate(parseISO(tomorrowsPort.date), { includeWeekday: true, shortFormat: true }),
        description: null,
        times: {
          arrival: tomorrowsPort.arrivalTime,
          departure: tomorrowsPort.departureTime
        },
        excursion: tomorrowsPort.excursion
      };
    }

    // Tomorrow is a sea day - check if it's the first one
    const tomorrow = addDays(startOfDay(new Date()), 1);
    const seaDayCount = countSeaDaysUpTo(cruise, tomorrow);
    const isFirstSeaDay = seaDayCount === 1;

    return {
      icon: Waves,
      iconColor: 'text-blue-300',
      title: 'Sea Day',
      subtitle: isFirstSeaDay ? 'Enjoy your first day at sea' : 'Another fun day at sea',
      description: null
    };
  };

  // Show skeleton until both data is loaded AND minimum time has elapsed
  // This ensures consistent loading experience across all platforms (web and native)
  if (!dataLoaded || !minTimeElapsed) {
    debug.log('[Dashboard] Showing DashboardSkeleton - data not loaded or min time not elapsed');
    return (
      <DashboardSkeleton />
    );
  }
  
  debug.log('[Dashboard] Rendering real dashboard content');

  const upcomingCruises = filteredCruises.filter(cruise => {
    if (activeCruise && cruise.id === activeCruise.id) return false;
    return getCruiseEndTime(cruise) > new Date();
  });
  
  const pastCruises = filteredCruises
    .filter(cruise => {
      if (activeCruise && cruise.id === activeCruise.id) return false;
      return getCruiseEndTime(cruise) <= new Date();
    })
    .sort((a, b) => parseISO(b.departureDate).getTime() - parseISO(a.departureDate).getTime());

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  const renderAdvertisement = () => (
    <motion.div
      variants={item}
      className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
    >
      <div className="h-40 relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <CustomCruiseIcon size={136} className="text-white/30" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent text-white">
          <h3 className="text-xl font-semibold">Ready for your next cruise?</h3>
          <p className="text-sm opacity-90">Book with Cruising Along</p>
        </div>
      </div>
      <div className="p-4 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Tag className="h-5 w-5 text-purple-500" />
            <span className="text-sm font-medium text-gray-900">
              Special offers available
            </span>
          </div>
          <a
            href="https://cruisingalong.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-purple-600 hover:text-purple-700 font-medium text-sm"
          >
            Book Now
            <ExternalLink className="h-4 w-4 ml-1" />
          </a>
        </div>
      </div>
    </motion.div>
  );

  const renderActiveCruise = () => {
    if (!activeCruise) return null;

    const shipImageUrl = getShipImageUrl(activeCruise.cruiseLine, activeCruise.shipName);
    const currentDay = getCurrentCruiseDay(activeCruise);
    const totalDays = differenceInDays(parseISO(activeCruise.returnDate), parseISO(activeCruise.departureDate));
    
    const todaysAgenda = getTodaysAgendaContent(activeCruise);
    const tomorrowsAgenda = getTomorrowsAgendaContent(activeCruise);

    return (
      <motion.div
        initial={rememberedScrollY > 0 ? false : { opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-xl shadow-md overflow-hidden mb-8 relative"
      >
        {/* Animated background waves */}
        <div className="absolute inset-0 opacity-10 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent transform -skew-x-12 animate-pulse"></div>
        </div>
        
        {/* Casino comp banner */}
        {activeCruise.isComplimentaryCasinoSailing && (
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-1 flex items-center justify-center">
            <Dice className="h-4 w-4 mr-2" />
            <span className="text-sm font-medium">Casino Comp Cruise</span>
          </div>
        )}

        <div className="relative z-10 p-6">
          {/* Compact Header: waves bubble left, text right for compressed mobile layout */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="flex-shrink-0 flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-full"
            >
              <Waves className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
            </motion.div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-semibold text-white leading-tight">You're Currently Sailing!</h2>
              <p className="text-blue-100 text-sm sm:text-base">
                {isReturnDay(activeCruise) ? 'Disembarkation Day' : `Day ${currentDay} of ${totalDays}`}
              </p>
              {activeCruiseCompanions.length > 0 && (
                <div className="mt-1.5 flex items-center gap-1.5">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-white/20 backdrop-blur-md rounded-full text-xs text-white font-medium border border-white/20">
                    <Users className="h-3.5 w-3.5 text-teal-300" />
                    <span>Sailing with: {activeCruiseCompanions.map((f) => f.friendDisplayName).join(', ')}</span>
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
            {/* Ship Details */}
            <div className="lg:col-span-1">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 h-full">
                <div className="flex items-center mb-3">
                  <CustomCruiseIcon size={34} className="text-blue-200 mr-2" />
                  <h3 className="text-lg font-semibold text-white">Your Ship</h3>
                </div>
                
                {shipImageUrl && !imageErrors[activeCruise.id] && (
                  <div className="mb-3 rounded-lg overflow-hidden">
                    <LazyImage src={shipImageUrl} 
                      alt={`${activeCruise.shipName}`}
                      className="w-full h-24 object-cover"
                      loading="lazy" 
                      decoding="async" 
                      fetchPriority="low" 
                      width={600} 
                      height={96} 
                      onError={() => {
                        setImageErrors(prev => ({ ...prev, [activeCruise.id]: true }));
                      }}
                    />
                  </div>
                )}
                
                <div className="space-y-2 text-white">
                  <div>
                    <p className="text-lg font-bold">{activeCruise.shipName}</p>
                    <p className="text-blue-200 text-sm">{activeCruise.cruiseLine}</p>
                  </div>
                  
                  {activeCruise.cabinNumber && (
                    <div className="flex items-center">
                      <Anchor className="h-4 w-4 text-blue-300 mr-2" />
                      <div>
                        <p className="font-medium text-sm">Your Cabin</p>
                        <p className="text-blue-200 text-sm">{activeCruise.cabinNumber} ({activeCruise.cabinType})</p>
                      </div>
                    </div>
                  )}
                  
                  {activeCruise.isGuaranteedCabin && (
                    <div className="flex items-center">
                      <Anchor className="h-4 w-4 text-blue-300 mr-2" />
                      <div>
                        <p className="font-medium text-sm">Your Cabin</p>
                        <p className="text-blue-200 text-sm">Guaranteed {activeCruise.cabinType}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Today's Itinerary */}
            <div className="lg:col-span-1">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 h-full">
                <div className="flex items-center mb-3">
                  <todaysAgenda.icon className={`h-5 w-5 ${todaysAgenda.iconColor} mr-2`} />
                  <h3 className="text-lg font-semibold text-white">Today's Agenda</h3>
                </div>
                
                <div className="space-y-2 text-white">
                  <div>
                    <p className={`text-base font-bold ${todaysAgenda.iconColor}`}>{todaysAgenda.title}</p>
                    <p className="text-blue-200 text-sm">{todaysAgenda.subtitle}</p>
                  </div>
                  
                  {todaysAgenda.description && (
                    <p className="text-blue-100 text-sm">{todaysAgenda.description}</p>
                  )}
                  
                  {todaysAgenda.times && (
                    <div className="space-y-1">
                      {todaysAgenda.times.arrival && (
                        <div className="flex items-center">
                          <Clock className="h-3 w-3 text-blue-300 mr-2" />
                          <span className="text-xs">Arrival: {formatTimeString(todaysAgenda.times.arrival)}</span>
                        </div>
                      )}
                      {todaysAgenda.times.departure && (
                        <div className="flex items-center">
                          <Clock className="h-3 w-3 text-blue-300 mr-2" />
                          <span className="text-xs">Departure: {formatTimeString(todaysAgenda.times.departure)}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {todaysAgenda.excursion && (
                    <div className="mt-2 p-2 bg-green-500/20 rounded-lg">
                      <p className="text-xs font-medium text-green-300">Planned Excursion:</p>
                      <p className="text-xs text-white">{todaysAgenda.excursion}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tomorrow's Itinerary - hidden on disembarkation day unless there is a back-to-back cruise */}
            {tomorrowsAgenda && (
              <div className="lg:col-span-1">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 h-full">
                  <div className="flex items-center mb-3">
                    <tomorrowsAgenda.icon className={`h-5 w-5 ${tomorrowsAgenda.iconColor} mr-2`} />
                    <h3 className="text-lg font-semibold text-white">
                      {isReturnDay(activeCruise) ? 'Up Next' : 'Tomorrow'}
                    </h3>
                  </div>
                  
                  <div className="space-y-2 text-white">
                    <div>
                      <p className={`text-base font-bold ${tomorrowsAgenda.iconColor}`}>{tomorrowsAgenda.title}</p>
                      <p className="text-blue-200 text-sm">{tomorrowsAgenda.subtitle}</p>
                    </div>
                    
                    {tomorrowsAgenda.description && (
                      <p className="text-blue-100 text-sm">{tomorrowsAgenda.description}</p>
                    )}
                    
                    {tomorrowsAgenda.times && (
                      <div className="space-y-1">
                        {tomorrowsAgenda.times.arrival && (
                          <div className="flex items-center">
                            <Clock className="h-3 w-3 text-blue-300 mr-2" />
                            <span className="text-xs">Arrival: {formatTimeString(tomorrowsAgenda.times.arrival)}</span>
                          </div>
                        )}
                        {tomorrowsAgenda.times.departure && (
                          <div className="flex items-center">
                            <Clock className="h-3 w-3 text-blue-300 mr-2" />
                            <span className="text-xs">Departure: {formatTimeString(tomorrowsAgenda.times.departure)}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {tomorrowsAgenda.excursion && (
                      <div className="mt-2 p-2 bg-yellow-500/20 rounded-lg">
                        <p className="text-xs font-medium text-yellow-300">Planned Excursion:</p>
                        <p className="text-xs text-white">{tomorrowsAgenda.excursion}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Action Button - Now inline as 4th column */}
            <div className="lg:col-span-1 flex flex-col items-center justify-center gap-3">
              {(() => {
                const shipLocationUrl = getShipLocationUrl(activeCruise.cruiseLine, activeCruise.shipName);
                const shipDeckplanUrl = getShipDeckplanUrl(activeCruise.cruiseLine, activeCruise.shipName);
                const hasLinks = shipLocationUrl || shipDeckplanUrl;
                
                return hasLinks && (
                  <div className="flex flex-col gap-2 w-full">
                    {shipLocationUrl && (
                      <a
                        href={shipLocationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center justify-center px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-sm w-full focus:outline-none focus:ring-2 focus:ring-accent ${
                          activeButton === 'shipLocation' ? 'bg-white/30' : ''
                        }`}
                        onTouchStart={() => setActiveButton('shipLocation')}
                        onTouchEnd={() => setActiveButton(null)}
                        onMouseDown={() => isNative && setActiveButton('shipLocation')}
                        onMouseUp={() => setActiveButton(null)}
                        onMouseLeave={() => setActiveButton(null)}
                      >
                        <Navigation className="h-4 w-4 mr-2" />
                        Track Ship Location
                      </a>
                    )}
                    {shipDeckplanUrl && (
                      <a
                        href={shipDeckplanUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center justify-center px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-sm w-full focus:outline-none focus:ring-2 focus:ring-accent ${
                          activeButton === 'deckPlan' ? 'bg-white/30' : ''
                        }`}
                        onTouchStart={() => setActiveButton('deckPlan')}
                        onTouchEnd={() => setActiveButton(null)}
                        onMouseDown={() => isNative && setActiveButton('deckPlan')}
                        onMouseUp={() => setActiveButton(null)}
                        onMouseLeave={() => setActiveButton(null)}
                      >
                        <MapPin className="h-4 w-4 mr-2" />
                        View Deck Plan
                      </a>
                    )}
                  </div>
                );
              })()}
              <Link
                to={`/cruise/${activeCruise.id}`}
                className="inline-flex items-center px-6 py-3 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-lg transition-all duration-200 backdrop-blur-sm border border-white/30 w-full justify-center"
              >
                <CustomCruiseIcon size={28} className="mr-2" />
                View Full Details
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderFriendCurrentlySailingBanner = () => {
    if (!SHOW_FRIENDS_BUTTON || currentlySailingFriendEntries.length === 0) return null;

    const separateEntries = activeCruise
      ? currentlySailingFriendEntries.filter(
          (ac) =>
            ac.cruise.shipName !== activeCruise.shipName ||
            ac.cruise.departureDate !== activeCruise.departureDate
        )
      : currentlySailingFriendEntries;

    if (separateEntries.length === 0) return null;

    const firstFriendName = separateEntries[0].friends[0]?.friendDisplayName ?? 'A friend';
    const totalFriendsCount = separateEntries.reduce((sum, ac) => sum + ac.friends.length, 0);

    const descriptionText =
      totalFriendsCount === 1
        ? `${firstFriendName} is currently sailing on ${separateEntries[0].cruise.shipName} (${separateEntries[0].cruise.cruiseLine})`
        : `${firstFriendName} and ${totalFriendsCount - 1} other friend${totalFriendsCount - 1 > 1 ? 's' : ''} are currently cruising`;

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-secondary-600 flex items-center justify-center text-white shrink-0 shadow-sm">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
              You have a friend currently cruising!
            </h3>
            <p className="text-xs sm:text-sm text-gray-600">{descriptionText}</p>
          </div>
        </div>
        <Link
          to="/friends"
          className="px-4 py-2 bg-secondary-600 hover:bg-secondary-500 text-white rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap self-stretch sm:self-auto text-center shrink-0"
        >
          View Friends Dashboard
        </Link>
      </motion.div>
    );
  };

  const renderCruiseCard = (cruise: Cruise, isPast: boolean = false) => {
    const shipImageUrl = getShipImageUrl(cruise.cruiseLine, cruise.shipName);
    // Show banner for past cruises that haven't been marked as completed
    const needsCompletion = isPast && !cruise.completed;
    const cardCompanions = getCompanionsForCruise(cruise.shipName, cruise.departureDate);
    
    // Function to save scroll position before navigation
    const handleCruiseClick = () => {
      sessionStorage.setItem('dashboardScrollY', window.scrollY.toString());
    };
    
    // Route to post-cruise-details if completion is needed, otherwise cruise details
    const cardDestination = needsCompletion 
      ? `/post-cruise-details/${cruise.id}`
      : `/cruise/${cruise.id}`;
    
    return (
      <Link 
        key={cruise.id}
        to={cardDestination}
        onClick={handleCruiseClick}
      >
        <motion.div 
          variants={item}
          className={`bg-white rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow duration-300 ${isPast ? 'opacity-80' : ''} relative`}
          whileHover={{ scale: 1.01 }}
          transition={{ duration: 0.2 }}
        >
        <div className="h-40 relative overflow-hidden">
          {/* Post Cruise Details Banner - positioned as overlay at top */}
          {needsCompletion && (
            <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1 flex items-center justify-center">
              <AlertCircle className="h-4 w-4 mr-2" />
              <span className="text-sm font-medium">Add post cruise details</span>
            </div>
          )}

          {/* Casino Comp Banner - hidden when post cruise details banner is present */}
          {cruise.isComplimentaryCasinoSailing && !needsCompletion && (
            <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-1 flex items-center justify-center">
              <Dice className="h-4 w-4 mr-2" />
              <span className="text-sm font-medium">Casino Comp</span>
            </div>
          )}

          {/* Traveling Companion Badge */}
          {cardCompanions.length > 0 && (
            <div
              className="absolute bottom-3 right-3 z-20 bg-white/20 backdrop-blur-md text-white px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 border border-white/30 shadow-[0_4px_12px_rgba(0,0,0,0.3)]"
              title={`${isPast ? 'Sailed with' : 'Sailing with'}: ${cardCompanions.map((c) => c.friendDisplayName).join(', ')}`}
            >
              <Users className="h-3.5 w-3.5 text-teal-300 drop-shadow-sm" />
              <span className="drop-shadow-sm">
                {isPast ? 'Sailed with ' : 'With '}
                {cardCompanions.length === 1
                  ? cardCompanions[0].friendDisplayName
                  : `${cardCompanions.length} friends`}
              </span>
            </div>
          )}
          
          {shipImageUrl && !imageErrors[cruise.id] ? (
            <>
              <LazyImage src={shipImageUrl} 
                alt={`${cruise.shipName} - ${cruise.cruiseLine}`}
                className="w-full h-full object-cover"
                onError={() => {
                  // Mark this image as failed so we can show fallback
                  setImageErrors(prev => ({ ...prev, [cruise.id]: true }));
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 h-2/3 bg-gradient-to-t from-black/60 to-transparent"></div>
            </>
          ) : (
            <>
              <div className={`absolute inset-0 ${isPast ? 'bg-gradient-to-r from-gray-700 to-gray-500' : 'bg-gradient-to-r from-blue-800 to-blue-600'}`}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <CustomCruiseIcon size={136} className="text-white/30" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-2/3 bg-gradient-to-t from-black/60 to-transparent"></div>
            </>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <h3 className="text-xl font-semibold truncate">{cruise.shipName}</h3>
            <p className="text-sm opacity-90">{cruise.cruiseLine}</p>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start space-x-2">
              <CalendarIcon className="h-5 w-5 text-teal-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {formatDate(parseISO(cruise.departureDate))}
                </p>
                <p className="text-xs text-gray-500">
                  {formatRelativeTime(parseISO(cruise.departureDate))}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end space-y-1 min-h-[3rem]">
              {cruise.cabinType && (
                <div className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                  {cruise.cabinType}
                </div>
              )}
              <div className={`px-2 py-1 text-xs font-medium rounded-full flex items-center ${
                cruise.completed 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-transparent'
              } ${cruise.completed ? 'visible' : 'invisible'}`}>
                <CheckCircle className="h-3 w-3 mr-1" />
                Completed
              </div>
            </div>
          </div>
          
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start space-x-2">
              <MapPin className="h-5 w-5 text-teal-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {cruise.departurePort}
                </p>
                <p className="text-xs text-gray-500">Departure Port</p>
              </div>
            </div>
            
            {/* Package Icons */}
            <div className="flex items-center space-x-1">
              {cruise.hasDrinkPackage && (
                <div className="flex items-center" title="Drink Package">
                  <GlassWater className="h-5 w-5 text-blue-800" />
                </div>
              )}
              {cruise.hasInternetPackage && (
                <div className="flex items-center" title="Internet Package">
                  <Wifi className="h-5 w-5 text-blue-800" />
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-between border-t border-gray-200 pt-4 mt-4">
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <Sunrise className="h-4 w-4" />
              <span>{formatDate(parseISO(cruise.departureDate), { shortFormat: true })}</span>
              <span>-</span>
              <SunsetIcon className="h-4 w-4" />
              <span>{formatDate(parseISO(cruise.returnDate))}</span>
            </div>
            
            <div className="flex items-center text-sm font-medium text-secondary-600 hover:text-secondary-500 transition-colors duration-200">
              <ExternalLink className="h-4 w-4 mr-1" />
              View Details
            </div>
          </div>
        </div>
      </motion.div>
      </Link>
    );
  };


  // When restoring scroll position, skip entrance animation to avoid flash of top or white
  const isRestoringScroll = rememberedScrollY > 0;

  return (
    <motion.div 
      className="space-y-8"
      initial={isRestoringScroll ? { opacity: 1 } : { opacity: 0 }}
      animate={{ opacity: dataLoaded ? 1 : 0 }}
      transition={{ duration: isRestoringScroll ? 0 : 0.3, ease: "easeOut" }}
    >
      {/* Welcome Back Modal - Using Portal */}
      {showWelcomeBack && recentCruise && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Welcome Back!</h2>
              <button
                onClick={dismissWelcomeBack}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
            <p className="text-gray-600">
              How was your cruise on the <strong>{recentCruise.shipName}</strong> with {recentCruise.cruiseLine}?
            </p>
            <p className="text-sm text-gray-500">
              Returned {formatDate(parseISO(recentCruise.returnDate), { fullMonth: true })}
            </p>
            
            <div className="space-y-3 pt-4">
              <button
                onClick={() => {
                  handleCruiseCompletion(recentCruise, true);
                  dismissWelcomeBack();
                }}
                className="w-full px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
              >
                Add Details
              </button>
              
              <button
                onClick={dismissWelcomeBack}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium"
              >
                Maybe Later
              </button>
            </div>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Completion Confirmation Modal - Using Portal */}
      {showCompletionModal && selectedCruiseForCompletion && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Delete Cruise</h2>
              <button
                onClick={() => {
                  setShowCompletionModal(false);
                  setSelectedCruiseForCompletion(null);
                }}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
            <p className="text-gray-600">
              Are you sure you want to delete this cruise from your history? This action cannot be undone.
            </p>
            <p className="text-sm text-gray-500">
              <strong>{selectedCruiseForCompletion.shipName}</strong> - {selectedCruiseForCompletion.cruiseLine}
            </p>
            
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setShowCompletionModal(false);
                  setSelectedCruiseForCompletion(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCruise}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-200 font-medium"
              >
                Delete
              </button>
            </div>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="w-full md:w-auto">
          <h1 className="text-3xl font-bold text-gray-800">My Cruises</h1>
          <p className="text-gray-600 mt-1">
            {cruises.length > 0
              ? activeCruise 
                ? `Currently sailing! ${upcomingCruises.length} more ${upcomingCruises.length === 1 ? 'cruise' : 'cruises'} planned`
                : `You have ${upcomingCruises.length} upcoming ${upcomingCruises.length === 1 ? 'cruise' : 'cruises'}`
              : 'Start by adding your first cruise'}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
          <div className="relative flex-grow lg:w-96 xl:w-[28rem]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search cruises, ports, travel details..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 ${searchTerm ? 'pr-10' : 'pr-4'} py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500`}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                type="button"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          <Link
            to="/add-cruise"
            className="flex items-center justify-center gap-2 bg-secondary-600 hover:bg-secondary-500 text-white px-4 py-2 rounded-md transition duration-200 whitespace-nowrap font-medium"
          >
            <PlusCircle className="h-5 w-5" />
            <span>Add Cruise</span>
          </Link>
        </div>
      </div>

      {/* Search Results Info */}
      {searchTerm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <Search className="h-5 w-5 text-blue-500 mr-2" />
            <p className="text-blue-800">
              {filteredCruises.length === 0 ? (
                <>No cruises found matching "<strong>{searchTerm}</strong>"</>
              ) : (
                <>Found {filteredCruises.length} cruise{filteredCruises.length !== 1 ? 's' : ''} matching "<strong>{searchTerm}</strong>"</>
              )}
            </p>
          </div>
          {searchTerm && filteredCruises.length > 0 && (
            <p className="text-primary-600 text-sm mt-1">
              Search includes cruise details, ports, excursions, and travel arrangements
            </p>
          )}
        </div>
      )}

      {cruises.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <CustomCruiseIcon size={108} className="text-teal-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">No cruises yet</h2>
          <p className="text-gray-600 mb-6">
            Start tracking your cruise vacations by adding your first cruise.
          </p>
          <Link
            to="/add-cruise"
            className="inline-flex items-center px-4 py-2 bg-secondary-600 hover:bg-secondary-500 text-white rounded-md transition duration-200 font-medium"
          >
            <PlusCircle className="h-5 w-5 mr-2" />
            Add Your First Cruise
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Next Cruise Countdown - Moved to top */}
          {nextCruise && (!searchTerm || filteredCruises.some(c => c.id === nextCruise.id)) && (
            <motion.div
              initial={isRestoringScroll ? false : { opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-lg shadow-md text-white overflow-hidden"
            >
              <div className="flex items-center justify-between px-4 py-2">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-blue-200 mr-2" />
                  <h3 className="text-sm font-semibold">Next Cruise Countdown</h3>
                </div>
                                 <div className="flex items-center">
                   <div className="flex items-center mr-4">
                     <div className="text-3xl font-bold">{daysUntilNextCruise}</div>
                     <span className="text-sm text-blue-200 ml-1">days</span>
                   </div>
                   <button
                     onClick={handleShareCountdown}
                     disabled={isGeneratingShare}
                     className="flex items-center px-3 py-1.5 bg-secondary-600 hover:bg-secondary-500 disabled:opacity-50 rounded-md transition-colors duration-200 text-sm font-medium text-white min-w-[80px] min-h-[32px] justify-center"
                   >
                     {isGeneratingShare ? (
                       <Loader2 className="h-4 w-4 animate-spin" />
                     ) : (
                       <>
                         <Share2 className="h-4 w-4 mr-1" />
                         Share
                       </>
                     )}
                   </button>
                 </div>
              </div>
              <div className="px-4 pb-3">
                <p className="text-sm text-blue-100">
                  {nextCruise.shipName} | {nextCruise.cruiseLine}
                  {nextCruiseItineraryTitle && ` | ${nextCruiseItineraryTitle}`}
                  {' | Departing '}{formatDate(parseISO(nextCruise.departureDate), { fullMonth: true })}
                </p>
              </div>
            </motion.div>
          )}

          {/* Active Cruise Section */}
          {(!searchTerm || (activeCruise && filteredCruises.some(c => c.id === activeCruise.id))) && renderActiveCruise()}

          {/* Friend Currently Sailing Section */}
          {!searchTerm && renderFriendCurrentlySailingBanner()}

          {(!searchTerm || upcomingCruises.length > 0 || settings?.showCruiseAd) && (
            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                {upcomingCruises.length > 0 ? 'Upcoming Cruises' : 'No Upcoming Cruises'}
              </h2>
            <motion.div 
             className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6"
              variants={container}
              initial={isRestoringScroll ? 'show' : 'hidden'}
              animate="show"
            >
              {settings?.showCruiseAd && renderAdvertisement()}
              {upcomingCruises.map((cruise) => renderCruiseCard(cruise))}
            </motion.div>
            </section>
          )}
          
          {/* Calendar Section - Moved between upcoming and past cruises */}
          {!searchTerm && (
            <section className="mb-10">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Cruise Calendar</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Calendar Column */}
                <div>
                  <div className="calendar-container">
                    <Calendar
                      onChange={setSelectedDate}
                      value={selectedDate}
                      calendarType={getCalendarType()}
                      tileClassName={tileClassName}
                      className="w-full rounded-lg border-none shadow-sm"
                    />
                  </div>
                </div>

                {/* Details Column */}
                <div className="space-y-4">
                  {/* Selected Date Cruises Card */}
                  <div className="p-4 bg-gray-50 rounded-lg h-fit">
                    <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      {selectedDate instanceof Date && formatDate(selectedDate, { fullMonth: true })}
                    </h3>
                    {selectedDateCruises.length > 0 ? (
                      <ul className="space-y-2">
                        {selectedDateCruises.map(cruise => (
                          <li key={cruise.id} className="text-sm">
                            <Link 
                              to={`/cruise/${cruise.id}`}
                              className="text-primary-600 hover:text-primary-500 transition-colors duration-200 flex items-center"
                            >
                              <CustomCruiseIcon size={16} className="mr-2 text-teal-500" />
                              {cruise.shipName} - {cruise.cruiseLine}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No cruises scheduled for this date</p>
                    )}
                  </div>

                  {/* Quick Stats Card */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="text-sm font-medium text-blue-900 mb-3 flex items-center">
                      <BarChart className="h-4 w-4 mr-2" />
                      Quick Stats
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-blue-700">Upcoming Cruises:</span>
                        <span className="font-semibold text-blue-900">{upcomingCruises.length}</span>
                      </div>
                      {activeCruise && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-blue-700">Currently Sailing:</span>
                          <span className="font-semibold text-blue-900">1</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-blue-700">Past Cruises:</span>
                        <span className="font-semibold text-blue-900">{pastCruises.length}</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                        <span className="text-sm font-medium text-blue-800">Total Cruises:</span>
                        <span className="font-bold text-blue-900">{cruises.length}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </section>
          )}

          {pastCruises.length > 0 && (
            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Past Cruises</h2>
              <motion.div 
               className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6"
                variants={container}
                initial={isRestoringScroll ? 'show' : 'hidden'}
                animate="show"
              >
                {pastCruises.slice(0, visiblePastCount).map((cruise) => renderCruiseCard(cruise, true))}
              </motion.div>
              {pastCruises.length > visiblePastCount && (
                <div className="flex justify-center mt-8">
                  <button onClick={() => setVisiblePastCount((c) => c + 10)} className="px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-500 font-medium transition duration-200">Load more</button>
                </div>
              )}
            </section>
          )}

        </div>
      )}

      {/* Share Modal - Using Portal */}
      {showShareModal && nextCruise && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 60 }}>
          <motion.div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-800">Share Your Countdown</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Format Selection */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Choose Format:</h4>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setShareFormat('feed')}
                  className={`p-3 rounded-lg border-2 transition-colors ${
                    shareFormat === 'feed'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div className="text-sm font-medium">Feed Post</div>
                  <div className="text-xs text-gray-500 mt-1">1200×630px</div>
                  <div className="text-xs text-gray-500">Facebook, X (Twitter), MMS</div>
                </button>
                <button
                  onClick={() => setShareFormat('stories')}
                  className={`p-3 rounded-lg border-2 transition-colors ${
                    shareFormat === 'stories'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div className="text-sm font-medium">Stories</div>
                  <div className="text-xs text-gray-500 mt-1">1080×1920px</div>
                  <div className="text-xs text-gray-500">Instagram, Facebook Stories</div>
                </button>
              </div>
            </div>
            
            {/* Generate Image Button */}
            <div className="mb-6">
              <button
                onClick={handleGenerateAndShare}
                disabled={isGeneratingShare}
                className="w-full flex items-center justify-center px-4 py-3 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isGeneratingShare ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Generating Image...
                  </>
                ) : (
                  <>
                    {Capacitor.isNativePlatform() ? (
                      <>
                        <Share2 className="h-5 w-5 mr-2" />
                        Select Format & Share
                      </>
                    ) : (
                      <>
                        <Download className="h-5 w-5 mr-2" />
                        Generate & Download Image
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
            
            {/* Sharing Options - Only show after image is generated */}
            {imageGenerated && (
              <>
                <p className="text-gray-600 mb-6">
                  Your countdown image has been downloaded! Choose how you'd like to share your excitement:
                </p>
                
                <div className="space-y-3">
                  {/* Native Share (if available) */}
                  {typeof navigator.share === 'function' && (
                    <button
                      onClick={handleNativeShare}
                      className="w-full flex items-center justify-center px-4 py-3 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition duration-200 font-medium"
                    >
                      <Share2 className="h-5 w-5 mr-2" />
                      Share via Device
                    </button>
                  )}
                  
                  {/* Social Media Options */}
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => handleSocialShare('twitter')}
                      className="flex items-center justify-center px-4 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition duration-200"
                    >
                      <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/>
                      </svg>
                      X (Twitter)
                    </button>
                    
                    <button
                      onClick={() => handleSocialShare('facebook')}
                      className="flex items-center justify-center px-4 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition duration-200 font-medium"
                    >
                      <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Facebook
                    </button>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800 flex items-start">
                      <Info className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Tip:</strong> For Instagram, upload the downloaded image manually to your story or post</span>
                    </p>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>,
        document.body
      )}

      {/* Hidden Share Image Components */}
      {nextCruise && (
        <>
          {/* Feed Format Image */}
          <div style={{ 
            position: 'fixed', 
            top: '-9999px', 
            left: '-9999px', 
            width: '1200px', 
            height: '630px', 
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: -1,
            display: shareFormat === 'feed' ? 'block' : 'none'
          }}>
            <ShareCountdownImageFeed
              ref={shareFormat === 'feed' ? shareImageRef : null}
              cruise={nextCruise}
              daysUntilDeparture={daysUntilNextCruise || 0}
              shipImageUrl={getShipImageUrl(nextCruise.cruiseLine, nextCruise.shipName)}
              itineraryTitle={nextCruiseItineraryTitle}
            />
          </div>
          
          {/* Stories Format Image */}
          <div style={{ 
            position: 'fixed', 
            top: '-9999px', 
            left: '-9999px', 
            width: '1080px', 
            height: '1920px', 
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: -1,
            display: shareFormat === 'stories' ? 'block' : 'none'
          }}>
            <ShareCountdownImageStories
              ref={shareFormat === 'stories' ? shareImageRef : null}
              cruise={nextCruise}
              daysUntilDeparture={daysUntilNextCruise || 0}
              shipImageUrl={getShipImageUrl(nextCruise.cruiseLine, nextCruise.shipName)}
              itineraryTitle={nextCruiseItineraryTitle}
            />
          </div>
        </>
      )}
    </motion.div>
  );
};

export default Dashboard;
